import { useEffect, useMemo, useState } from "react";
import { feedApi, type FeedEvent } from "../auth/api";
import { buildMatches, netPnlSince } from "../lib/pnl";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export interface SevenDayPnl {
  loading: boolean;
  /** Net P&L over the last 7 days, USDC base units (6-dec). Signed:
   *  positive = up, negative = down, 0n = flat / no recent matches. */
  pnlMinor: bigint;
}

/**
 * Real 7-day profit/loss for a wallet, derived from the same on-chain
 * indexer feed the profile page uses (no balance-history table needed —
 * we sum settled match outcomes in the window). Drives the wallet card's
 * delta + sparkline direction.
 */
export function useSevenDayPnl(wallet: string | null): SevenDayPnl {
  const [events, setEvents] = useState<FeedEvent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!wallet) {
      setEvents([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    feedApi
      .userHistory(wallet, 200)
      .then((r) => !cancelled && setEvents(r.events))
      .catch(() => !cancelled && setEvents([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [wallet]);

  const pnlMinor = useMemo(() => {
    if (!wallet) return 0n;
    const since = Date.now() - SEVEN_DAYS_MS;
    return netPnlSince(buildMatches(events, wallet.toLowerCase()), since);
  }, [events, wallet]);

  return { loading, pnlMinor };
}
